import {Tag} from 'folder-items/FileConstants';
import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaFieldColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toColumnDefinition from 'notebook/structured-tables/columns/toColumnDefinition';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import getArchivedRows from 'notebook/structured-tables/util/getArchivedRows';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {
  convertTypedValueToTag,
  formatTypedValue,
  getProcessTaskIdFromCell,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {EVALUATED_TABLE_ERROR_OPTIONS} from 'processes/ProcessConstants';
import {getInitialTag, getTagsToSet} from 'schemas/tags/TagUtil';
import {getStudyIdsFromTypedValue} from 'studies/studyable/StudiesStructuredTableColumn';
import {warn} from 'util/Logging';

import {CreateOrUpdateServerProcessTask, CreateTaskForNewProcess} from '../dao/ProcessManagementDAO';
import {ProcessTaskStructuredTable} from './ProcessTaskStructuredTableAdapter';

export default function taskTableToTasks({
  table,
  evaluatedTable,
  processId,
  creationOriginId,
  sourceType,
  actionOrigin,
  actionMethod,
  structuredTableRowsBatch,
  ignoreErrors,
}: {
  table: ProcessTaskStructuredTable;
  evaluatedTable: EvaluatedTable;
  processId?: string;
  creationOriginId?: string;
  sourceType: string;
  actionOrigin: ActionOrigin;
  actionMethod: ActionMethod;
  structuredTableRowsBatch?: Array<StructuredTableRow<unknown>>;
  ignoreErrors?: boolean;
}): Array<CreateOrUpdateServerProcessTask | CreateTaskForNewProcess> {
  const shouldIgnoreErrors = ignoreErrors ?? false;
  if (!shouldIgnoreErrors) {
    if (
      doesEvaluatedTableHaveAnyErrors(evaluatedTable, {
        ...EVALUATED_TABLE_ERROR_OPTIONS,
        archivedRows: table.columns.some((col) => col.columnType === TableColumnType.PROCESS_TASK)
          ? getArchivedRows(table, evaluatedTable, structuredTableRowsBatch)
          : [],
      })
    ) {
      warn('Attempting to convert a task table with errors', {
        extra: {
          evaluatedTable,
          table,
        },
      });
    }
  }

  return evaluatedTable.map((row) => {
    let taskId: string | undefined;
    let assigneeId: string | undefined | null;
    let statusId: string | undefined;
    let scheduledOnDate: string | null | undefined;
    let studyIds: Array<string> | undefined;
    const tags: Array<Tag> = [];
    for (const [colIndex, structuredTableColumn] of table.columns.entries()) {
      const {columnType} = structuredTableColumn;
      const cell = row[colIndex];
      if (columnType === TableColumnType.PROCESS_TASK) {
        const cellTaskId = getProcessTaskIdFromCell({
          text: formatTypedValue(cell.value),
          typedValue: cell.value,
        });
        if (cellTaskId && cellTaskId.length > 0) {
          taskId = cellTaskId;
        }
      } else if (columnType === TableColumnType.PROCESS_TASK_ASSIGNEE) {
        if (cell.value.type === BaseTypedValueType.USER) {
          assigneeId = cell.value.id;
        } else {
          // Any value that hasn't been resolved as a USER typedValue will be treated as NULL (i.e., no assignee)
          assigneeId = null;
        }
      } else if (columnType === TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE) {
        scheduledOnDate = cell.value.type === BaseTypedValueType.DATE ? cell.value.value : null;
      } else if (columnType === TableColumnType.PROCESS_TASK_STATUS) {
        if (cell.value.type === BaseTypedValueType.TASK_STATUS) {
          statusId = cell.value.id;
        }
      } else if (columnType === TableColumnType.TAG_VALUE) {
        const columnDefinition = toColumnDefinition({
          structuredTableColumn,
          schemas: table.schemas,
        }) as SchemaFieldColumnDefinition;
        // Guard against deleted (not archived) schema fields
        if (columnDefinition.schemaField != null) {
          const tag = convertTypedValueToTag(cell.value, columnDefinition.schemaField);
          if (tag != null) {
            tags.push(tag);
          } else {
            tags.push(getInitialTag(columnDefinition.schemaField));
          }
        }
      } else if (columnType === TableColumnType.STUDIES) {
        studyIds = getStudyIdsFromTypedValue(cell.value);
      }
    }
    const fields = getTagsToSet(tags);
    if (taskId == null) {
      return {
        processId,
        fields,
        assigneeId,
        creationOriginId: processId,
        scheduledOnDate,
        sourceType,
        actionOrigin,
        actionMethod,
        studyIds,
      };
    } else {
      return {
        id: taskId,
        statusId,
        fields,
        assigneeId,
        creationOriginId,
        scheduledOnDate,
        sourceType,
        actionOrigin,
        actionMethod,
        studyIds,
      };
    }
  });
}
